import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import CheckELogo from "@/components/CheckELogo";

/**
 * LoginPage — Clean Enterprise restyle.
 * Same chrome as the landing page so the visitor never feels they've
 * left the Xpert site. Right-hand pane carries a quiet brand testimonial
 * + a 1-line product hook back to /get-verified.
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });

  // Where to send the user after a successful login. We honour ?return_to=...
  // for the OAuth-consent flow (BR-006): a user clicks "Add custom connector"
  // in Claude.ai → backend /oauth/authorize 302s to /oauth/consent → consent
  // page detects no user → routes here with return_to=/oauth/consent?...
  // After login, we must land them back on the consent page, not the dashboard.
  const returnTo = searchParams.get("return_to");

  // Validate return_to is a same-origin relative path (no open-redirect).
  const safeReturnTo = (() => {
    if (!returnTo) return null;
    try {
      const decoded = decodeURIComponent(returnTo);
      // Only allow internal paths — block "https://evil.com/..." style returns.
      if (decoded.startsWith("/") && !decoded.startsWith("//")) return decoded;
      // Allow absolute URLs only if they point at our own origins.
      const url = new URL(decoded);
      if (url.hostname === "xpert.company" || url.hostname === "api.xpert.company") {
        return decoded;
      }
    } catch { /* not a parseable URL, ignore */ }
    return null;
  })();

  const goAfterLogin = (u) => {
    if (safeReturnTo) {
      // Use window.location for absolute URLs (e.g. api.xpert.company/oauth/authorize)
      // and navigate() for in-app paths.
      if (safeReturnTo.startsWith("http")) {
        window.location.href = safeReturnTo;
      } else {
        navigate(safeReturnTo, { replace: true });
      }
      return;
    }
    navigate(u.is_expert ? "/expert-dashboard" : "/", { replace: true });
  };

  // If the user is ALREADY logged in (e.g. came here from Claude.ai OAuth
  // dance with a live session) and we have a return_to, ship them straight
  // through. No extra "click to login again" friction.
  useEffect(() => {
    if (user && safeReturnTo) {
      goAfterLogin(user);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, safeReturnTo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      const u = await login(formData.email.toLowerCase().trim(), formData.password);
      toast.success("Welcome back!");
      goAfterLogin(u);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas text-ink font-body flex flex-col">
      <MarketingNav />

      <main className="flex-1 grid lg:grid-cols-2">
        {/* Left — form */}
        <div className="flex items-center justify-center px-4 sm:px-6 py-12 sm:py-16">
          <div className="w-full max-w-md">
            <h1 className="font-display text-4xl sm:text-5xl text-ink leading-tight">
              Welcome back.
            </h1>
            <p className="text-slateline-600 mt-3">
              Sign in to submit work for review and see your written verdicts.
            </p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <div>
                <label htmlFor="email"
                       className="text-xs font-semibold uppercase tracking-wider text-slateline-500">
                  Email
                </label>
                <div className="mt-2 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slateline-400" />
                  <input
                    id="email" type="email" placeholder="you@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                    className="w-full pl-9 pr-3 h-11 bg-white border border-slateline-200
                               rounded-md text-ink placeholder-slateline-400 outline-none
                               focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password"
                       className="text-xs font-semibold uppercase tracking-wider text-slateline-500">
                  Password
                </label>
                <div className="mt-2 relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slateline-400" />
                  <input
                    id="password" type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                    className="w-full pl-9 pr-10 h-11 bg-white border border-slateline-200
                               rounded-md text-ink placeholder-slateline-400 outline-none
                               focus:border-brand-green focus:ring-2 focus:ring-brand-green/20 transition-colors"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slateline-400 hover:text-ink">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center h-11 rounded-md
                           bg-brand-green hover:bg-brand-greenHover disabled:opacity-60
                           text-white font-semibold text-sm tracking-wide shadow-sm transition-colors"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Sign in"
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slateline-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-canvas text-slateline-400 uppercase tracking-wider font-semibold">
                  or
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                // Where Google should send us back to after a successful auth.
                // Default = /dashboard. But if we arrived here from an OAuth-
                // consent flow (BR-006), `safeReturnTo` is set — pass it
                // through so the user lands back on the consent page, not the
                // dashboard, after the Google round-trip.
                let postAuthUrl;
                if (safeReturnTo) {
                  // safeReturnTo may be an absolute URL (api.xpert.company/...)
                  // or a relative path. Either way, hand it to the backend's
                  // OAuth callback verbatim — the backend will redirect there.
                  postAuthUrl = safeReturnTo.startsWith("http")
                    ? safeReturnTo
                    : window.location.origin + safeReturnTo;
                } else {
                  postAuthUrl = window.location.origin + "/dashboard";
                }
                const backendUrl = process.env.REACT_APP_BACKEND_URL || "https://api.xpert.company";
                window.location.href = `${backendUrl}/api/auth/google?redirect_uri=${encodeURIComponent(postAuthUrl)}`;
              }}
              className="w-full inline-flex items-center justify-center gap-3 h-11 rounded-md
                         border border-slateline-300 bg-white hover:bg-slateline-50
                         text-ink font-medium text-sm transition-colors"
              data-testid="google-signin-btn"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div className="mt-6 flex flex-col gap-2 text-sm text-slateline-500 text-center">
              <Link to="/forgot-password" className="inline-flex items-center justify-center gap-1.5 text-brand-green hover:underline">
                <KeyRound className="w-3.5 h-3.5" />
                Forgot password?
              </Link>
              <div>
                Don't have an account?{" "}
                <Link to="/register" className="text-brand-green hover:underline font-medium">
                  Create one
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Right — quiet brand pane */}
        <div className="hidden lg:flex items-center justify-center bg-ink text-white relative overflow-hidden">
          <div aria-hidden="true"
               className="absolute -top-32 -right-24 w-[500px] h-[500px] rounded-full bg-brand-green/30 blur-3xl" />
          <div className="max-w-md p-12 relative">
            <CheckELogo size={48} />
            <h2 className="mt-8 font-display text-3xl leading-snug">
              The human review layer for AI-built work.
            </h2>
            <p className="mt-5 text-white/70 text-lg leading-relaxed">
              Send AI-built work for a written review. Today, Ankit Kumar,
              Xpert's founder, writes every verdict.
            </p>

            <div className="mt-12 pt-6 border-t border-white/10 text-sm text-white/70">
              Not a member yet?{" "}
              <Link to="/get-verified" className="text-brand-green font-semibold hover:underline">
                Submit your first AI output →
              </Link>
            </div>
          </div>
        </div>
      </main>

      <MarketingFooter />
    </div>
  );
}
